import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import DOMPurify from 'dompurify';
import { Helmet } from 'react-helmet';
import BASE_URL from './apibaseurl/ApiBaseUrl';


export default function BlogDetails() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    AOS.init({ duration: 800, once: true, offset: 100 });
  }, []);

  useEffect(() => {
    if (slug) {
      setError(null);
      fetch(`${BASE_URL}/blog_detail?slug=${slug}`)
        .then(res => res.json())
        .then(data => {
          if (data.status === 1) {
            setBlog(data.data);
          } else {
            setError('Blog post not found.');
          }
        })
        .catch(err => {
          console.error("Error fetching blog detail:", err);
          setError('Failed to load blog post. Please try again later.');
        });
    }
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen flex justify-center items-center text-lg text-red-600">
        {error}
      </div>
    );
  }

  if (!blog) {
    return (
      <div
        className="min-h-screen flex justify-center items-center text-lg"
        aria-live="polite"
        role="status"
      >
        Loading...
      </div>
    );
  }

  const sanitizedDescription = DOMPurify.sanitize(blog.description, {
    ADD_TAGS: ['img', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'br', 'strong', 'span', 'a'],
    ADD_ATTR: ['alt', 'style', 'loading', 'src', 'height', 'width', 'href', 'target', 'dir', 'class'],
    ALLOW_DATA_ATTR: false,
    KEEP_CONTENT: true
  });

  return (
    <>
      <Helmet>
        <title>{blog.title} | Your Blog Name</title>
        <meta
          name="description"
          content={`${blog.title} - Learn how QR codes can boost your company’s efficiency and productivity.`}
        />
      </Helmet>

      <section className="flex flex-col bg-white mx-auto max-w-7xl">
        <section className="flex flex-col" data-aos="fade-up">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-normal justify-between px-6 py-8 gap-20">
            <div
              className="max-w-xl space-y-5 lg:pt-16"
              data-aos="fade-right"
              data-aos-delay="100"
            >
              <nav className="flex items-center space-x-2 text-lg Bebas-neue" aria-label="Breadcrumb">
                <span className="text-[#234042]">HOME</span>
                <span className="h-4 w-[1px] bg-gray-400" aria-hidden="true"></span>
                <span className="text-[#F47321] tracking-tight">BLOG</span>
              </nav>
              <h1 className="text-[#234042] tracking-wide lg:text-6xl text-3xl !leading-tight !font-normal Bebas-neue">
                {blog.title}
              </h1>
            </div>
            <div className="relative lg:pt-16" data-aos="zoom-in" data-aos-delay="200">
              <img
                src={blog.picture}
                alt={blog.title}
                className="w-full h-auto rounded-lg shadow-md md:w-auto"
                // style={{ maxWidth: '450px',  }}
                // height="400" 
                loading="lazy" // Native lazy loading
              />
            </div>
          </div>
        </section>
      </section>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 space-y-12 helvetica-neue pb-20">
          <div
            className="blog-content text-[#234042] leading-relaxed text-lg prose prose-lg max-w-none"
            data-aos="fade-up"
            dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
          />
        </div>
      </section>

      {/* Custom styles for blog content */}
      <style jsx>{`
        .blog-content :where(p) {
          margin-bottom: 1.5rem;
          line-height: 1.8;
        }
        .blog-content :where(h1,h2,h4,h5,h6 ,strong,span,p) {
          font-size: revert !important;
          font-weight: revert !important;
        }
        .blog-content :where(h3) {
          font-size: 1.5rem;
          font-weight: 700;
          margin-top: 2rem;
          margin-bottom: 1rem;
          color: #234042;
        }
        .blog-content :where(img) {
          width: 100%;
          max-width: 800px;
          height: auto;
          margin: 1.5rem auto;
          display: block;
          border-radius: 8px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          loading: lazy;
        }
        @media (max-width: 640px) {
          .blog-content :where(img) {
            width: 100% !important;
          }
        }
        .blog-content :where(p:empty) {
          display: none;
        }
            `}</style>
    </>
  );
}